import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Loader2, LogOut, Mail, MailOpen, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/hud/Logo";
import { http, errText } from "@/lib/http";
import Editor from "./editor/Editor";
import { CvManager } from "./CvManager";
import { Stats } from "./Stats";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

const Login = ({ onLogged }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogged(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="tile mx-auto mt-[12vh] w-full max-w-sm space-y-5 p-7" data-testid="admin-login-form">
      <div className="flex items-center gap-3">
        <Logo className="h-10 w-10" />
        <div>
          <h1 className="font-display text-xl text-[#F7F4EE]">Espace privé</h1>
          <p className="text-xs text-[#C5BDB3]">Messages reçus via le téléphone rétro</p>
        </div>
      </div>
      <label className="field"><span>E-mail</span>
        <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label className="field"><span>Mot de passe</span>
        <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      {error && <p data-testid="admin-login-error" className="text-sm text-[#F2A38A]">{error}</p>}
      <button data-testid="admin-login-submit" disabled={busy} className="btn-gold w-full justify-center">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Se connecter
      </button>
    </form>
  );
};

const MessageCard = ({ m, onToggle, onDelete }) => (
  <article data-testid={`admin-message-${m.id}`} className={`tile space-y-3 p-5 ${m.read ? "opacity-75" : "border-[#E69C24]/40"}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-display text-lg text-[#F7F4EE]">
          {!m.read && <span className="h-2 w-2 rounded-full bg-[#E69C24]" />}{m.name}
        </p>
        <a href={`mailto:${m.email}`} className="text-sm text-[#E6B877] hover:underline">{m.email}</a>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="mr-2 font-mono text-[11px] text-[#B5AB9E]">{fmt(m.created_at)}</span>
        <button data-testid={`admin-toggle-read-${m.id}`} onClick={() => onToggle(m)} className="icon-btn" aria-label={m.read ? "Marquer non lu" : "Marquer lu"}>
          {m.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
        </button>
        <button data-testid={`admin-delete-${m.id}`} onClick={() => onDelete(m)} className="icon-btn is-close" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#D9CFC2]">{m.message}</p>
  </article>
);

const Inbox = ({ onLogout }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/messages");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error(errText(e));
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (m) => {
    const { data } = await http.patch(`/admin/messages/${m.id}`, { read: !m.read });
    setItems((xs) => xs.map((x) => (x.id === m.id ? data : x)));
  };
  const remove = async (m) => {
    if (!window.confirm(`Supprimer le message de ${m.name} ?`)) return;
    await http.delete(`/admin/messages/${m.id}`);
    setItems((xs) => xs.filter((x) => x.id !== m.id));
    toast.success("Message supprimé");
  };
  const unread = (items || []).filter((m) => !m.read).length;

  return (
    <div>
      <div className="mb-5 flex justify-end">
        <button onClick={load} className="chip" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" />Actualiser</button>
      </div>
      <p data-testid="admin-unread-count" className="mb-4 text-xs text-[#C5BDB3]">{items ? `${items.length} message(s) · ${unread} non lu(s)` : "Chargement…"}</p>
      {items === null ? (
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#E69C24]" />
      ) : items.length === 0 ? (
        <p data-testid="admin-empty" className="tile p-8 text-center text-sm text-[#C5BDB3]">Aucun message pour le moment.</p>
      ) : (
        <div className="space-y-3">{items.map((m) => <MessageCard key={m.id} m={m} onToggle={toggle} onDelete={remove} />)}</div>
      )}
    </div>
  );
};

const SourceExport = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await http.get("/admin/source", { responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chambre-noire-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archive téléchargée");
    } catch (e) {
      toast.error("Téléchargement impossible, réessayez.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="tile max-w-2xl space-y-5 p-6 sm:p-8" data-testid="source-export">
      <h2 className="font-display text-xl text-[#F7F4EE]">Télécharger tout le site</h2>
      <p className="text-sm leading-relaxed text-[#C5BDB3]">
        L'archive contient le code complet (chambre 3D, version classique, espace privé, serveur), votre contenu actuel et toutes les images envoyées.
        Le guide <span className="text-[#F7F4EE]">HEBERGEMENT.md</span> à l'intérieur explique comment l'installer sur votre propre serveur.
      </p>
      <ul className="space-y-1.5 text-sm text-[#D9CFC2]">
        <li>• Les mots de passe et clés secrètes ne sont pas inclus : un fichier <span className="font-mono text-xs">.env.example</span> indique quoi remplir.</li>
        <li>• En auto-hébergement, les images sont stockées dans un dossier du serveur.</li>
        <li>• Re-téléchargez l'archive après chaque modification importante pour avoir la dernière version.</li>
      </ul>
      <button onClick={download} disabled={busy} className="btn-gold" data-testid="source-download-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{busy ? "Préparation…" : "Télécharger le code (.zip)"}
      </button>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("content");
  const tabCls = (t) => `shrink-0 rounded-full px-4 py-2 text-sm transition-colors ${tab === t ? "bg-[#E69C24] text-[#12100E]" : "text-[#F7F4EE] hover:bg-[#F7F4EE]/8"}`;
  return (
    <div className="mx-auto w-full max-w-6xl px-1 py-8 sm:px-5 sm:py-12">
      <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Logo className="h-10 w-10" />
          <div>
            <h1 className="font-display text-2xl text-[#F7F4EE]">Espace privé</h1>
            <p className="text-xs text-[#C5BDB3]">{user.email}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/" className="chip" data-testid="admin-back-room"><ArrowLeft className="h-4 w-4" />La chambre</Link>
          <button onClick={onLogout} className="chip" data-testid="admin-logout"><LogOut className="h-4 w-4" />Déconnexion</button>
        </div>
      </header>
      <nav className="dock no-scrollbar mb-6 inline-flex max-w-full gap-1 overflow-x-auto p-1">
        <button className={tabCls("content")} onClick={() => setTab("content")} data-testid="admin-tab-content">Contenu du site</button>
        <button className={tabCls("cv")} onClick={() => setTab("cv")} data-testid="admin-tab-cv">Mon CV</button>
        <button className={tabCls("stats")} onClick={() => setTab("stats")} data-testid="admin-tab-stats">Statistiques</button>
        <button className={tabCls("messages")} onClick={() => setTab("messages")} data-testid="admin-tab-messages">Messages</button>
        <button className={tabCls("source")} onClick={() => setTab("source")} data-testid="admin-tab-source">Code source</button>
      </nav>
      {tab === "content" && <Editor />}
      {tab === "cv" && <CvManager />}
      {tab === "stats" && <Stats />}
      {tab === "messages" && <Inbox onLogout={onLogout} />}
      {tab === "source" && <SourceExport />}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() =>
      http.post("/auth/refresh").then((r) => setUser(r.data)).catch(() => setUser(false)));
  }, []);
  useEffect(() => {
    if (user) { try { localStorage.setItem("noTrack", "1"); } catch { /* stockage indisponible */ } }
  }, [user]);
  const logout = useCallback(async () => {
    try { await http.post("/auth/logout"); } catch { /* déjà déconnecté */ }
    setUser(false);
  }, []);

  return (
    <div className="min-h-screen bg-[#12100E] px-4" data-testid="admin-page">
      {user === null && <Loader2 className="mx-auto mt-[30vh] h-6 w-6 animate-spin text-[#E69C24]" />}
      {user === false && <Login onLogged={setUser} />}
      {user && <Dashboard user={user} onLogout={logout} />}
    </div>
  );
}
